import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { downloadCsv } from '../../lib/api';
import { useAuth } from '../../stores/auth';

export interface CsvExport {
  /** The list endpoint the screen reads, e.g. '/bills'. */
  path: string;
  /** The filters the screen is showing (page / perPage are dropped). */
  query?: Record<string, unknown>;
  /** The permission page whose `export` action guards it, e.g. 'bills'. */
  page: string;
  filename?: string;
}

/**
 * "Export CSV" — every row of the list as it is filtered now, in the user's scope (Task R). Allowed only for a user
 * holding `<page>.export`; the server checks it again. Shared by the table toolbar button and the page's ⋯ menu.
 */
export function useCsvExport({ path, query, page, filename }: CsvExport) {
  const allowed = useAuth((s) => s.can(`${page}.export`));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const run = async () => {
    if (busy) return;
    setBusy(true); setNote(null);
    try {
      const r = await downloadCsv(path, query ?? {}, filename);
      setNote(r.truncated ? `First ${r.rows.toLocaleString('en-GB')} rows` : null);
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Export failed');
    } finally { setBusy(false); }
  };
  return { allowed, busy, note, run };
}

export function ExportCsvButton({ className = '', ...exp }: CsvExport & { className?: string }) {
  const { allowed, busy, note, run } = useCsvExport(exp);
  if (!allowed) return null;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <button type="button" onClick={() => void run()} disabled={busy} className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-2.5 text-[12px] font-medium text-ink-600 transition hover:bg-ink-50 hover:text-ink-900 disabled:opacity-50" title="Download every row as it is filtered now">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}Export CSV
      </button>
      {note && <span className="text-[11px] text-ink-400">{note}</span>}
    </span>
  );
}
